import { useDispatch } from "react-redux"
import { useParams } from "react-router-dom"
import React, { useEffect, useState } from "react"
import { showSnackBar } from "../../components/Snackbar/Snackbar"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import { signIn, signInInternal } from "../../features/auth/authSlice"
import { ACCESS_TOKEN, REDIRECT_AFTER_LOGIN, REFRESH_TOKEN, StorageSetItem, USER } from "../../lib/storage"
import { sameOriginPath } from "../../lib/safeRedirect"
import { clearAmbientSelection } from "../../lib/ambientHeaders"
import { changePassword, completePasswordReset, validateSetupToken, resendSetupLink } from "../../features/password/password"
import { setSentryUser } from "../../lib/sentryUser"
import { getPasswordSetupToken, clearPasswordSetupToken } from "../../lib/passwordSetupToken"
import { AMBIGUOUS_EMAIL_CODE } from "../../lib/authErrorCodes"
import { NEUTRAL_CREATE_PASSWORD_CSS } from "../../components/NeutralAuth/neutralCardCss"
import { TEAM_LINK_TYPES as TEAM_TYPES } from "../../lib/portalAudience"
import NeutralPortalHeader from "../../components/NeutralAuth/NeutralPortalHeader"

const MIN_PASSWORD_LENGTH = 5

const STATE_CHECKING = 'checking'
const STATE_READY = 'ready'
const STATE_INVALID = 'invalid'
const STATE_ALREADY_SET = 'alreadySet'
const PURPOSE_RESET = 'reset'

const signInPathFor = (type) => (TEAM_TYPES.includes(type) ? '/team-portal' : '/')

const CreatePassword = ({ neutral = false }) => {
  const dispatch = useDispatch()

  // Seeded from the route: the no-token early return and the generic invalid branch both skip
  // setUserType, and a null there sends every affiliate and staff member to the customer portal.
  // The server's answer still wins when it arrives.
  const { type: typeFromRoute } = useParams()
  const token = getPasswordSetupToken()

  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [error, setError] = useState("")
  const [resendError, setResendError] = useState("")
  const [linkState, setLinkState] = useState(STATE_CHECKING)
  const [userType, setUserType] = useState(typeFromRoute ?? null)
  const [resendEmail, setResendEmail] = useState("")
  const [resendSent, setResendSent] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isReset, setIsReset] = useState(false)
  const { triggerLoading } = useMyContextLoading()

  const checkLink = async () => {
    if (!token) {
      setLinkState(STATE_INVALID)
      return
    }

    try {
      triggerLoading(true)
      const resultAction = await dispatch(validateSetupToken(token))
      const result = resultAction.payload?.data

      if (result?.valid) {
        setUserType(result.userType)
        setIsReset(result.purpose === PURPOSE_RESET)
        setLinkState(STATE_READY)
      } else if (result?.reason === STATE_ALREADY_SET) {
        if (result.userType) setUserType(result.userType)
        setLinkState(STATE_ALREADY_SET)
      } else if (!result) {
        // The check did not answer — an outage, or a backend predating this endpoint. Calling that
        // an expired link would push every invitee to resend, superseding the token they hold.
        // Let the save attempt decide instead.
        setLinkState(STATE_READY)
      } else {
        clearPasswordSetupToken()
        setLinkState(STATE_INVALID)
      }
    } catch (e) {
      setLinkState(STATE_READY)
    }
    triggerLoading(false)
  }

  const handleResend = async () => {
    if (!resendEmail.trim()) {
      setResendError("*Enter the email your invitation was sent to")
      return
    }

    triggerLoading(true)
    const resultAction = await dispatch(resendSetupLink(resendEmail.trim()))
    triggerLoading(false)

    if (resendSetupLink.rejected.match(resultAction)) {
      setResendError("We couldn't send a new link just now. Please try again in a moment.")
      return
    }

    setResendError("")
    setResendSent(true)
  }

  const handleSavePassword = async () => {
    if (isSaving) return

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`*Password must be at least ${MIN_PASSWORD_LENGTH} characters long`)
      return
    }

    if (password !== confirmPassword) {
      showSnackBar("error", "Passwords do not match")
      return
    }

    setIsSaving(true)
    triggerLoading(true)

    if (isReset) {
      await saveResetPassword()
      return
    }

    const response = await dispatch(changePassword({ token, newPassword: password }))

    if (response?.payload?.data?.email) {
      clearPasswordSetupToken()
      await handleSignIn({ email: response.payload.data.email, type: response.payload.data.userType || userType })
    } else {
      const message = response?.payload?.message
        || (typeof response?.payload === 'string' ? response.payload : null)
        || "Something went wrong, please try again later"
      // 409 is "this account already has a password", and resend only issues links for accounts
      // without one — so sign-in, not the box that promises a mail and then sends nothing.
      if (response?.payload?.status === 409) {
        setLinkState(STATE_ALREADY_SET)
        return
      }

      showSnackBar("error", message)
      // Only a refusal of the link itself retires the form: a 500 or dropped connection leaves the
      // token unspent, so the resend box would destroy a link that still works.
      if (response?.payload?.status === 403) {
        setLinkState(STATE_INVALID)
      }
    }

    setIsSaving(false)
    triggerLoading(false)
  }

  const saveResetPassword = async () => {
    const response = await dispatch(completePasswordReset({ token, newPassword: password }))

    if (completePasswordReset.fulfilled.match(response)) {
      clearPasswordSetupToken()
      window.location.href = `${signInPathFor(response.payload?.data?.userType || userType)}?notice=password-reset`
      return
    }

    showSnackBar("error", response?.payload?.message || "Something went wrong, please try again later")
    if (response?.payload?.status === 403) {
      clearPasswordSetupToken()
      setLinkState(STATE_INVALID)
    }

    setIsSaving(false)
    triggerLoading(false)
  }

  const saveTokens = async ({ accessToken, refreshToken, user }, type) => {
    // A different person is now signed in on this browser, without a logout ever running. The brand
    // and office belong to whoever was here before, and leaving them means the new user acts as the
    // previous user's firm until something refuses them.
    clearAmbientSelection()

    await StorageSetItem(ACCESS_TOKEN, accessToken)
    await StorageSetItem(REFRESH_TOKEN, refreshToken)
    await StorageSetItem(USER, user)
    setSentryUser(user)

    const redirectUrl = sameOriginPath(localStorage.getItem(REDIRECT_AFTER_LOGIN))
    localStorage.removeItem(REDIRECT_AFTER_LOGIN)
    if (redirectUrl) {
      window.location.href = redirectUrl
    } else {
      window.location.href = TEAM_TYPES.includes(type) ? '/team-portal' : '/'
    }
  }

  const handleSignIn = async ({ email, type }) => {
    const credentials = { email, password }
    const action = TEAM_TYPES.includes(type) ? signInInternal : signIn
    const response = await dispatch(action(credentials))

    if (action.fulfilled.match(response)) {
      const data = response.payload
      await saveTokens(data.data, type)
    } else if (action.rejected.match(response)) {
      // The password IS set and the link is spent, so leaving the user on an unchanged form invites
      // a second Save and a 403. Sign-in can still fail for unrelated reasons — a non-active portal
      // status, a duplicate account — so say what happened and hand them to sign-in, which raises
      // the toast this navigation would otherwise discard.
      //
      // Keyed off the backend's error code, never its wording: an ambiguous email is the one failure
      // signing in again cannot clear, so "please sign in to continue" would be a dead end.
      const notice = response.meta?.code === AMBIGUOUS_EMAIL_CODE
        ? 'password-set-contact-admin'
        : 'password-set'
      triggerLoading(false)
      window.location.href = `${signInPathFor(type)}?notice=${notice}`
    }
  }

  useEffect(() => {
    checkLink()
  }, [])

  useEffect(() => {
    setError('')
  }, [password])

  useEffect(() => {
    setResendError('')
  }, [resendEmail])

  useEffect(() => {
    if (neutral) return
    const link = document.createElement('link')
    link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap'
    link.rel = 'stylesheet'
    document.head.appendChild(link)
  }, [neutral])

  const welcomeHeading = neutral ? 'Welcome' : 'Welcome to MDL'
  const iconStroke = neutral ? 'currentColor' : 'rgba(201,169,110,0.6)'

  return (
      <>
        <style>{neutral ? NEUTRAL_CREATE_PASSWORD_CSS : `
          .cp-page {
            min-height: 100vh;
            background: #0f0f0f;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: 'DM Sans', sans-serif;
          }
          .cp-card {
            background: #161616;
            border: 1px solid rgba(255,255,255,0.07);
            border-radius: 16px;
            padding: 48px;
            width: 100%;
            max-width: 420px;
          }
          .cp-icon-wrap {
            display: flex;
            justify-content: center;
            margin-bottom: 24px;
          }
          .cp-heading {
            color: #ffffff;
            font-size: 24px;
            font-weight: 500;
            text-align: center;
            margin: 0 0 8px 0;
          }
          .cp-subtitle {
            color: rgba(255,255,255,0.45);
            font-size: 13px;
            text-align: center;
            margin: 0 0 36px 0;
          }
          .cp-field {
            display: flex;
            flex-direction: column;
            gap: 8px;
            margin-bottom: 20px;
          }
          .cp-label {
            color: rgba(255,255,255,0.45);
            font-size: 11px;
            font-weight: 500;
            letter-spacing: 0.1em;
            text-transform: uppercase;
          }
          .cp-input {
            background: rgba(255,255,255,0.05);
            border: 1px solid rgba(255,255,255,0.1);
            border-radius: 10px;
            color: #ffffff;
            padding: 14px 18px;
            font-size: 14px;
            font-family: 'DM Sans', sans-serif;
            outline: none;
            width: 100%;
            box-sizing: border-box;
            transition: border-color 0.2s;
          }
          .cp-input:focus {
            border-color: rgba(201,169,110,0.45);
          }
          .cp-error {
            color: #ef4444;
            font-size: 12px;
            margin-top: 4px;
          }
          .cp-btn {
            width: 100%;
            background: #c9a96e;
            color: #0f0f0f;
            border: none;
            border-radius: 10px;
            padding: 15px;
            font-size: 14px;
            font-weight: 500;
            font-family: 'DM Sans', sans-serif;
            cursor: pointer;
            margin-top: 12px;
            transition: background 0.2s;
          }
          .cp-btn:hover {
            background: #d4b47a;
          }
          .cp-link-btn {
            width: 100%;
            background: none;
            border: none;
            color: rgba(255,255,255,0.45);
            font-size: 13px;
            font-family: 'DM Sans', sans-serif;
            cursor: pointer;
            margin-top: 16px;
            padding: 4px;
          }
          .cp-link-btn:hover {
            color: rgba(255,255,255,0.75);
          }
        `}</style>
        <div className="cp-page">
          {neutral && <NeutralPortalHeader />}
          <div className="cp-card">
            <div className="cp-icon-wrap">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M24 4L8 12V24C8 33.6 15.2 42.5 24 44C32.8 42.5 40 33.6 40 24V12L24 4Z" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="24" cy="22" r="4" stroke={iconStroke} strokeWidth="2"/>
                <path d="M24 26V32" stroke={iconStroke} strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>

            {linkState === STATE_CHECKING && (
              <>
                <h1 className="cp-heading">{welcomeHeading}</h1>
                <p className="cp-subtitle">Checking your invitation…</p>
              </>
            )}

            {linkState === STATE_READY && (
              <>
                <h1 className="cp-heading">{isReset ? 'Reset your password' : welcomeHeading}</h1>
                <p className="cp-subtitle">{isReset ? 'Choose a new password' : 'Set your password'}</p>
                <div className="cp-field">
                  <label className="cp-label">Password</label>
                  <input
                    className="cp-input"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  {error && <span className="cp-error">{error}</span>}
                </div>
                <div className="cp-field">
                  <label className="cp-label">Confirm Password</label>
                  <input
                    className="cp-input"
                    type="password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>
                <button className="cp-btn" onClick={handleSavePassword}>
                  Save Password
                </button>
              </>
            )}

            {linkState === STATE_ALREADY_SET && (
              <>
                <h1 className="cp-heading">You already have a password</h1>
                <p className="cp-subtitle">
                  This account is already set up. Sign in with the password you chose, or reset it
                  if you don't remember it.
                </p>
                <button className="cp-btn" onClick={() => { window.location.href = signInPathFor(userType) }}>
                  Go to sign in
                </button>
              </>
            )}

            {linkState === STATE_INVALID && !resendSent && (
              <>
                <h1 className="cp-heading">This link is no longer valid</h1>
                <p className="cp-subtitle">
                  Setup links can be used once and expire after a few days. Enter your email and
                  we'll send you a new one.
                </p>
                <div className="cp-field">
                  <label className="cp-label">Email</label>
                  <input
                    className="cp-input"
                    type="email"
                    placeholder="Enter your email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                  />
                  {resendError && <span className="cp-error">{resendError}</span>}
                </div>
                <button className="cp-btn" onClick={handleResend}>
                  Send me a new link
                </button>
                <button className="cp-link-btn" onClick={() => { window.location.href = signInPathFor(userType) }}>
                  Already have a password? Sign in
                </button>
              </>
            )}

            {linkState === STATE_INVALID && resendSent && (
              <>
                <h1 className="cp-heading">Check your email</h1>
                <p className="cp-subtitle">
                  If an account is waiting to be set up for that address, a new link is on its way.
                </p>
                <button className="cp-link-btn" onClick={() => { window.location.href = signInPathFor(userType) }}>
                  Back to sign in
                </button>
              </>
            )}
          </div>
        </div>
      </>
  )
}

export default CreatePassword
